import type { BankExcluded, ReassignmentPreview, TeamJournal, TransferPreview } from "@hakili/contracts";
import { formatDate, formatDateTime, pl, t } from "@hakili/i18n";
import { Button, Callout, Modal, Select } from "@hakili/ui";
import { AlertTriangle, ArrowRight, Info } from "lucide-react";
import { useEffect, useState } from "react";

import { asSentence } from "../sentence.js";
import { lineStageWords } from "../stage-words.js";
import { DASH, fill } from "./dossiers.js";
import { GuardedAct } from "./guarded-act.js";
import { ModalCancel } from "./modal-cancel.js";
import type { FichierBanque } from "./dossiers.js";

/**
 * `EQUIPE-GESTURES`, round 2 (WF-BNK-25 steps 3, 4, 5 and 7; frames 80, 85, 86, 88, 89, 117, 118): **the chef's three gestures on « Équipe »,
 * each by the door of round 1.** The unfolded portfolio, the reassignment, the portfolio transfer and the journal.
 *
 * Every figure and every sentence is the door's: the proposal, its motive, the load a target would reach, the analysts set aside. The
 * server's motive is set as a sentence (a capital and one stop, the words unchanged); an analyst set aside is written HERE from her typed
 * `kind` and `returnsOn`, with the date through the shared formatter , the server's own string, which holds the date in ISO form, is the
 * audit's and is never printed. No seniority is read or said, and no « repère » is printed: neither exists in the data.
 */
export interface GestureDoors {
  readonly previewReassign: (fileId: string) => Promise<ReassignmentPreview>;
  readonly reassign: (fileId: string, toUserId: string, runId: string | null) => Promise<void>;
  readonly previewTransfer: (userId: string) => Promise<TransferPreview>;
  readonly transfer: (userId: string) => Promise<void>;
}

const REASSIGN_REASON = "equipe-reaffecter-raison";
const TRANSFER_REASON = "equipe-transfert-raison";

/**
 * D2's button (designer's ruling, call 1): « Transférer les {N} dossiers », the count from the data; « Transférer le dossier » for one.
 * « Transférer tout son portefeuille » is the ROW's label and never the window's: with no file the window has no transfer button.
 */
const transferLabel = (count: number): string =>
  count === 1
    ? t("bank.equipe.transfertBoutonUn")
    : fill("bank.equipe.transfertBouton", { n: String(count) });

/** « 28 octobre » , D2's form in « Écartés » (the year is dropped: the return is read against the day it is read). */
const dayWords = (day: string): string => {
  const instant = new Date(`${day}T12:00:00Z`);
  return Number.isNaN(instant.getTime()) ? day : formatDate(instant).replace(/\s\d{4}$/u, "");
};

/**
 * « Écartés : A (absent·e jusqu'au 28 octobre 2026) · B (invitation pas encore acceptée). » , from the typed kind, never the server's string.
 * A departed analyst is NOT named (designer's review `7f265f3d`: he is not a candidate, and the list keeps him greyed and dated).
 */
export function excludedWords(excluded: readonly BankExcluded[]): string {
  const named = excluded.filter((one) => one.kind !== "desactive");
  if (named.length === 0) return "";
  const parts = named.map((one) =>
    one.kind === "absent"
      ? fill("bank.equipe.ecarteAbsent", {
          nom: one.fullName,
          date: one.returnsOn === null ? t("bank.equipe.sonRetour") : dayWords(one.returnsOn),
        })
      : fill("bank.equipe.ecarteInvite", { nom: one.fullName }),
  );
  return fill("bank.equipe.ecartes", { liste: parts.join(" · ") });
}

/**
 * **Why nobody can take it, said by the causes PRESENT** (founder's ruling E3): the others are absent, or have not accepted their
 * invitation, or both, or no one is set aside (then no cause is named). `forMember` is the transfer's wording (frame 118).
 */
export function noAnalystWords(excluded: readonly BankExcluded[], forMember: string | null): string {
  const absent = excluded.some((one) => one.kind === "absent");
  const invited = excluded.some((one) => one.kind === "invitationEnAttente");
  if (forMember === null) {
    if (absent && invited) return t("bank.equipe.aucunLesDeux");
    if (absent) return t("bank.equipe.aucunAbsents");
    return invited ? t("bank.equipe.aucunInvites") : t("bank.equipe.aucunSansCause");
  }
  const named = { nom: forMember };
  if (absent && invited) return fill("bank.equipe.transfertAucunLesDeux", named);
  if (absent) return fill("bank.equipe.transfertAucunAbsents", named);
  return invited
    ? fill("bank.equipe.transfertAucunInvites", named)
    : fill("bank.equipe.transfertAucunSansCause", named);
}

/** A figure and its unit never part at a line's end: « (0 point) », not « (0 / point) ». */
export const bindPoints = (text: string): string => text.replace(/(\d+) (points?)/gu, "$1\u00A0$2");

const failureWords = (failure: unknown): string =>
  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee");

/** Frames 85 and 88 (the proposal, the chef's own pick) and 117 (nobody to give it to). */
export function ReassignModal(props: {
  readonly file: FichierBanque;
  readonly doors: GestureDoors;
  readonly onClose: () => void;
}): React.JSX.Element {
  const [preview, setPreview] = useState<ReassignmentPreview | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [choice, setChoice] = useState<string>("");
  const [working, setWorking] = useState(false);
  const { previewReassign } = props.doors;
  useEffect(() => {
    let live = true;
    previewReassign(props.file.id)
      .then((read) => {
        if (!live) return;
        setPreview(read);
        setChoice(read.proposal.assigneeUserId ?? read.targets[0]?.userId ?? "");
      })
      .catch((cause: unknown) => {
        if (live) setFailure(failureWords(cause));
      });
    return () => {
      live = false;
    };
  }, [previewReassign, props.file.id]);

  const give = (): void => {
    if (preview === null || choice === "") return;
    setWorking(true);
    props.doors
      .reassign(props.file.id, choice, preview.proposal.runId)
      .then(props.onClose)
      .catch((cause: unknown) => {
        setFailure(failureWords(cause));
        setWorking(false);
      });
  };
  const nobody = preview !== null && preview.targets.length === 0;
  // Frame 85 (R6): the « Réaffecter à » select carries the focus when the proposal is read, with the ring (a script focus after a pointer
  // click is not « visible » by default, so it asks for it).
  const selectable = preview !== null && !nobody;
  useEffect(() => {
    if (!selectable) return;
    // R7: the window opened on « Annuler »; the select takes the focus when the proposal is read, unless she has already moved on.
    const here = document.activeElement;
    if (
      here !== null &&
      here !== document.body &&
      here.getAttribute("data-testid") !== "equipe-reaffecter-annuler"
    )
      return;
    document.getElementById("equipe-reaffecter-a")?.setAttribute("data-initial-focus", "true");
    (document.getElementById("equipe-reaffecter-a") as HTMLSelectElement | null)?.focus({
      focusVisible: true,
    } as FocusOptions);
  }, [selectable]);
  const setAside = preview === null ? "" : excludedWords(preview.excluded);
  return (
    <Modal
      open
      title={fill("bank.equipe.reaffecterTitre", { nom: props.file.clientName ?? DASH })}
      onClose={props.onClose}
      cross={false}
      size="lg"
      actions={
        <>
          <ModalCancel testId="equipe-reaffecter-annuler" focusOnOpen onClick={props.onClose} />
          {preview === null || nobody || choice === "" ? (
            <GuardedAct
              testId="equipe-reaffecter-confirmer"
              reasonId={REASSIGN_REASON}
              reason={preview === null ? null : t("bank.equipe.aucuneCible")}
            >
              {t("bank.equipe.reaffecter")}
            </GuardedAct>
          ) : (
            <Button variant="ink" disabled={working} data-testid="equipe-reaffecter-confirmer" onClick={give}>
              {t("bank.equipe.reaffecter")}
            </Button>
          )}
        </>
      }
    >
      <div data-testid="equipe-reaffecter">
        {preview !== null ? (
          <p className="mb-3 text-sm text-gray-500" data-testid="equipe-reaffecter-sous">
            {fill("bank.equipe.reaffecterSous", {
              reference: preview.file.reference,
              chez: preview.carrier.fullName,
            })}
          </p>
        ) : null}
        {failure !== null ? (
          <Callout
            tone="bad"
            className="mb-3"
            testId="equipe-reaffecter-refus"
            icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
          >
            {failure}
          </Callout>
        ) : null}
        {preview === null ? (
          failure === null ? (
            <p id={REASSIGN_REASON} className="text-sm text-gray-500">
              {t("bank.session.chargement")}
            </p>
          ) : null
        ) : nobody ? (
          <div>
            <Callout
              tone="warn"
              className="mb-3"
              testId="equipe-reaffecter-aucun"
              icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
            >
              <p className="font-semibold">{t("bank.equipe.aucunAnalyste")}</p>
              <p>{noAnalystWords(preview.excluded, null)}</p>
            </Callout>
          </div>
        ) : (
          <Callout
            tone="info"
            className="mb-3"
            testId="equipe-proposition"
            icon={<Info className="h-4 w-4" aria-hidden="true" />}
          >
            <p className="font-semibold">
              {fill("bank.equipe.propositionTitre", { nom: preview.proposal.assigneeFullName ?? DASH })}
            </p>
            <p data-testid="equipe-proposition-motif">{bindPoints(asSentence(preview.proposal.motif))}</p>
          </Callout>
        )}
        {setAside === "" ? null : (
          <p className="mb-3 text-sm text-gray-500" data-testid="equipe-ecartes">
            {setAside}
          </p>
        )}
        {preview !== null && !nobody ? (
          <div className="mb-3">
            <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="equipe-reaffecter-a">
              {t("bank.equipe.reaffecterA")}
            </label>
            <Select
              id="equipe-reaffecter-a"
              testId="equipe-reaffecter-a"
              className="w-full"
              value={choice}
              onChange={setChoice}
              options={preview.targets.map((one) => ({
                value: one.userId,
                label: fill("bank.equipe.cibleOption", {
                  nom: one.fullName,
                  points: bindPoints(pl(one.points, "point")),
                }),
              }))}
            />
          </div>
        ) : null}
        <p className="text-sm text-gray-500">{t("bank.equipe.reaffecterAide")}</p>
      </div>
    </Modal>
  );
}

/** Frame 86 (the proposal file by file) and 118 (nobody to give them to). */
export function TransferModal(props: {
  readonly memberId: string;
  readonly memberName: string;
  readonly doors: GestureDoors;
  readonly onClose: () => void;
}): React.JSX.Element {
  const [preview, setPreview] = useState<TransferPreview | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const { previewTransfer } = props.doors;
  useEffect(() => {
    let live = true;
    previewTransfer(props.memberId)
      .then((read) => {
        if (live) setPreview(read);
      })
      .catch((cause: unknown) => {
        if (live) setFailure(failureWords(cause));
      });
    return () => {
      live = false;
    };
  }, [previewTransfer, props.memberId]);

  const go = (): void => {
    setWorking(true);
    props.doors
      .transfer(props.memberId)
      .then(props.onClose)
      .catch((cause: unknown) => {
        setFailure(failureWords(cause));
        setWorking(false);
      });
  };
  const count = preview?.lines.length ?? 0;
  const none = preview?.impossible === true;
  // D2 draws the callout only when EVERY file lacks a target; when some do, the guarded act's reason alone says so.
  const allLack = count > 0 && (preview?.lines ?? []).every((line) => line.targetUserId === null);
  // The source is never a candidate (designer's review `7f265f3d`, C2): she is not a cause of « aucun analyste ».
  const candidates = (preview?.excluded ?? []).filter((one) => one.userId !== props.memberId);
  return (
    <Modal
      open
      title={fill("bank.equipe.transfertTitre", { nom: props.memberName })}
      onClose={props.onClose}
      cross={false}
      size="lg"
      actions={
        <>
          <ModalCancel testId="equipe-transfert-annuler" focusOnOpen onClick={props.onClose} />
          {preview === null ? (
            <GuardedAct testId="equipe-transfert-confirmer" reasonId={TRANSFER_REASON} reason={null}>
              {t("bank.equipe.transfererTout")}
            </GuardedAct>
          ) : count === 0 ? null : none ? (
            <GuardedAct
              testId="equipe-transfert-confirmer"
              reasonId={TRANSFER_REASON}
              reason={t(allLack ? "bank.equipe.transfertSansCible" : "bank.equipe.transfertSansCibleUn")}
            >
              {transferLabel(count)}
            </GuardedAct>
          ) : (
            <Button variant="ink" disabled={working} data-testid="equipe-transfert-confirmer" onClick={go}>
              {transferLabel(count)}
            </Button>
          )}
        </>
      }
    >
      <div data-testid="equipe-transfert">
        <p className="mb-3 text-sm text-gray-500">{t("bank.equipe.transfertSous")}</p>
        {failure !== null ? (
          <Callout
            tone="bad"
            className="mb-3"
            testId="equipe-transfert-refus"
            icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
          >
            {failure}
          </Callout>
        ) : null}
        {preview === null ? (
          failure === null ? (
            <p id={TRANSFER_REASON} className="text-sm text-gray-500">
              {t("bank.session.chargement")}
            </p>
          ) : null
        ) : allLack ? (
          <div>
            <Callout
              tone="warn"
              className="mb-3"
              testId="equipe-transfert-aucun"
              icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
            >
              <p className="font-semibold">{t("bank.equipe.aucunAnalyste")}</p>
              <p>{noAnalystWords(candidates, props.memberName)}</p>
            </Callout>
          </div>
        ) : count === 0 ? (
          <p className="mb-3 text-sm text-gray-500">{t("bank.equipe.aucunDossierMotif")}</p>
        ) : null}
        {preview !== null && count > 0 ? (
          <ul className="mb-3 mt-3.5 grid gap-2" data-testid="equipe-transfert-lignes">
            {preview.lines.map((line) => (
              <li
                key={line.fileId}
                className="grid grid-cols-[minmax(0,1fr)_20px_minmax(0,1.4fr)] items-center gap-2.5 rounded-[10px] border border-[color:var(--hk-line)] px-2.5 py-2"
                data-testid={`equipe-transfert-ligne-${line.fileId}`}
              >
                <span className="min-w-0 flex-1 text-sm">
                  <b>{line.clientName ?? DASH}</b>
                  <span className="text-gray-500"> · {lineStageWords(line.stage)}</span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                <span className="min-w-0 flex-1 text-sm">
                  {line.targetFullName === null ? (
                    <b aria-hidden="true">{"\u2212"}</b>
                  ) : (
                    <b>{line.targetFullName}</b>
                  )}
                  <span className="text-gray-500"> · {bindPoints(asSentence(line.motif))}</span>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Modal>
  );
}

/** One journal line after the name and the reference: de A vers B · [transfert de portefeuille ·] par C · [the motive], one stop. */
const journalWords = (line: TeamJournal["lines"][number]): string => {
  const parts = [fill("bank.equipe.journalLigne", { de: line.fromName ?? DASH, vers: line.toName ?? DASH })];
  if (line.origin === "portfolio-transfer") parts.push(t("bank.equipe.journalTransfert"));
  if (line.byName !== null) parts.push(fill("bank.equipe.journalPar", { nom: line.byName }));
  if (line.origin !== "portfolio-transfer" && line.motif.trim() !== "") parts.push(line.motif.trim());
  const text = bindPoints(parts.join(" · "));
  return /[.!?…]$/u.test(text) ? text : `${text}.`;
};

/** Frames 80 (empty) and 89 (filled): one line per moved file, whatever the origin, the latest first (WF-BNK-25 step 7). D2's card: title, sub, the step tag at the right. */
export function JournalCard(props: { readonly journal: TeamJournal | null }): React.JSX.Element {
  const lines = props.journal?.lines ?? [];
  return (
    <section
      className="mb-4 overflow-hidden rounded-[var(--hk-r-lg)] border border-[color:var(--hk-line)] bg-[color:var(--hk-surface)]"
      aria-labelledby="equipe-journal-titre"
      data-testid="equipe-journal"
    >
      <div className="flex items-center gap-3 px-5 pb-1 pt-4">
        <h2 id="equipe-journal-titre" className="text-[length:var(--hk-fs-lead)] font-semibold leading-[1.3]">
          {t("bank.equipe.journalTitre")}
        </h2>
        <small className="text-[length:var(--hk-fs-body)] text-[color:var(--hk-text-2)]">
          {t("bank.equipe.journalSous")}
        </small>
        <span className="flex-1" />
      </div>
      <div className="px-5 pb-[18px] pt-3">
        {lines.length === 0 ? (
          <p className="text-[color:var(--hk-text-2)]" data-testid="equipe-journal-vide">
            {t("bank.equipe.journalVide")}
          </p>
        ) : (
          <ul className="m-0 grid list-none gap-2.5 p-0">
            {lines.map((line, index) => (
              <li
                key={`${line.fileId}-${line.at}-${String(index)}`}
                className="grid grid-cols-[130px_1fr] gap-3 text-[length:var(--hk-fs-body)]"
                data-testid="equipe-journal-ligne"
              >
                <time
                  className="font-[family-name:var(--hk-mono)] text-[color:var(--hk-text-2)]"
                  dateTime={line.at}
                >
                  {formatDateTime(new Date(line.at))}
                </time>
                <span className="min-w-0">
                  <b>{line.clientName ?? DASH}</b> ({line.reference}) {journalWords(line)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
